import React from 'react';
import { Button } from './ui/button';
import { ArrowRight, Award, TrendingUp, Shield, CheckCircle2 } from 'lucide-react';

const Hero = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const highlights = [
    { label: 'MNRE Approved', icon: Award },
    { label: '10 MW Capacity', icon: TrendingUp },
    { label: 'Grid Compliant', icon: Shield },
    { label: 'Zero Emissions', icon: CheckCircle2 }
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-white via-emerald-50/30 to-white">
      {/* Animated background elements */}
      <div className="absolute inset-0 z-0">
        {/* Gradient orbs */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle at 1px 1px, rgb(6, 78, 59) 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Column - Main Content */}
          <div className="space-y-8 text-center lg:text-left">
            {/* Badge */}
            <div className="inline-block">
              <div className="bg-emerald-50 text-emerald-700 px-4 py-2 rounded-full text-sm font-semibold border border-emerald-200">
                India's Leading Solar Infrastructure Developer
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-gray-900 leading-tight">
              Powering India's
              <span className="block mt-2 bg-gradient-to-r from-emerald-700 to-emerald-600 bg-clip-text text-transparent">
                Clean Energy Future
              </span>
            </h1>

            {/* Sub-headline */}
            <p className="text-xl text-gray-600 leading-relaxed">
              Building utility-scale solar infrastructure that delivers reliable, sustainable power to communities across India. Government-ready execution with a commitment to excellence.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start pt-4">
              <Button 
                size="lg" 
                className="bg-emerald-700 hover:bg-emerald-800 text-white px-8 py-6 text-lg font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
                onClick={() => scrollToSection('project')}
              >
                Our Project
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-emerald-700 text-emerald-700 hover:bg-emerald-50 px-8 py-6 text-lg font-medium rounded-lg transition-all duration-300"
                onClick={() => scrollToSection('about')}
              >
                About TenSolar
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="pt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
              {highlights.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="flex flex-col items-center lg:items-start gap-2">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-5 h-5 text-emerald-700" />
                    </div>
                    <span className="text-xs font-medium text-gray-700 text-center lg:text-left">{item.label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right Column - Visual Card */}
          <div className="relative">
            {/* Main Card */}
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100">
              {/* Realistic Solar Panel Image/Illustration */}
              <div className="relative h-96 bg-gradient-to-br from-white via-emerald-50/30 to-amber-50/30 overflow-hidden">
                {/* Very light background */}
                <div className="absolute inset-0 bg-gradient-to-b from-emerald-50/20 via-white to-amber-50/20" />
                
                {/* Solar panel array with harmonious colors */}
                <div className="absolute inset-0 p-8 flex items-center justify-center">
                  {/* Solar panel frame/mounting */}
                  <div className="relative w-full max-w-md">
                    {/* Panel array container with perspective */}
                    <div 
                      className="bg-gradient-to-br from-gray-100 via-gray-50 to-gray-100 rounded-lg shadow-lg p-2 border border-gray-200"
                      style={{
                        transform: 'perspective(1000px) rotateX(5deg)',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.08)'
                      }}
                    >
                      {/* Solar panel grid with soft emerald tones */}
                      <div className="grid grid-rows-4 gap-1">
                        {[...Array(4)].map((_, rowIndex) => (
                          <div key={rowIndex} className="grid grid-cols-8 gap-1">
                            {[...Array(8)].map((_, colIndex) => (
                              <div 
                                key={colIndex}
                                className="relative bg-gradient-to-br from-emerald-100 via-gray-100 to-emerald-50 rounded-sm overflow-hidden"
                                style={{
                                  boxShadow: 'inset 0 1px 2px rgba(6,78,59,0.1), 0 1px 1px rgba(0,0,0,0.05)',
                                  aspectRatio: '1'
                                }}
                              >
                                {/* Solar cell grid lines */}
                                <div className="absolute inset-0">
                                  <div className="absolute top-1/2 left-0 right-0 h-px bg-emerald-200/50" />
                                  <div className="absolute left-1/2 top-0 bottom-0 w-px bg-emerald-200/50" />
                                </div>
                                {/* Soft glass reflection effect */}
                                <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent" />
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Soft sunlight effect */}
                <div className="absolute top-6 right-6 w-40 h-40 bg-amber-100/40 rounded-full blur-3xl" />
                
                {/* Center overlay card */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/98 backdrop-blur-sm rounded-2xl p-8 shadow-2xl border border-gray-200 max-w-xs">
                    <div className="text-center">
                      <div className="text-6xl font-bold text-emerald-700 mb-2">10 MW</div>
                      <div className="text-base text-gray-700 font-semibold mb-1">Solar Power Plant</div>
                      <div className="text-sm text-gray-500">Madhya Pradesh, India</div>
                      
                      {/* Mini stats */}
                      <div className="mt-6 pt-6 border-t border-gray-200 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Capacity Factor:</span>
                          <span className="font-semibold text-gray-900">19%</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Annual Output:</span>
                          <span className="font-semibold text-gray-900">15M+ kWh</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Bottom stats section */}
              <div className="p-6 bg-gray-50">
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="text-2xl font-bold text-emerald-700">40,000+</div>
                    <div className="text-xs text-gray-600 mt-1">Solar Modules</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="text-2xl font-bold text-amber-700">50</div>
                    <div className="text-xs text-gray-600 mt-1">Acres</div>
                  </div>
                  <div className="text-center p-4 bg-white rounded-xl shadow-sm border border-gray-100">
                    <div className="text-2xl font-bold text-blue-700">25</div>
                    <div className="text-xs text-gray-600 mt-1">Year Warranty</div>
                  </div>
                </div>

                {/* Status indicator */}
                <div className="flex items-center justify-between px-4 py-3 bg-emerald-50 rounded-lg border border-emerald-100">
                  <span className="text-sm font-medium text-gray-700">Current Status:</span>
                  <span className="flex items-center gap-2 text-emerald-700 font-semibold text-sm">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    Operational & Grid-Connected
                  </span>
                </div>
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -top-4 -right-4 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white px-6 py-3 rounded-full shadow-xl z-10">
              <div className="text-sm font-bold">100% Clean</div>
              <div className="text-xs opacity-90">Zero Emissions</div>
            </div>

            {/* Decorative element */}
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-amber-200/30 rounded-full blur-2xl" />
          </div>
        </div>

        {/* Bottom info bar */}
        <div className="mt-16 pt-8 border-t border-gray-200">
          <div className="flex flex-wrap justify-center lg:justify-between items-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Registered with MNRE</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>State DISCOM Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>ISO Certified Operations</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>25-Year Performance Warranty</span>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-emerald-200 to-transparent" />
    </section>
  );
};

export default Hero;
